import React, { useEffect, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import { getClients, getPodcasts, getEpisodes } from "./api";

// ---- DATE FORMATTER ----
const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d; // fallback

    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const toLocalYmd = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const formatPublishDateLabel = (d) => {
    if (!d) return "";
    const text = String(d).slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return d;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (text === toLocalYmd(today)) return "Today";
    if (text === toLocalYmd(tomorrow)) return "Tomorrow";
    return formatDate(text);
};

const formatEpisodeCodeLabel = (seasonNumber, episodeNumber) => {
    const episode = Number.parseInt(episodeNumber, 10);
    if (!Number.isFinite(episode) || episode <= 0) return "";

    const season = Number.parseInt(seasonNumber, 10);
    if (Number.isFinite(season) && season > 0) {
        return `S${season}E${episode}`;
    }

    return `E${episode}`;
};

const isDistributionEnabled = (value) => {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
};

const formatTime = (t) => {
    if (!t || typeof t !== "string") return "";
    if (t.includes(":")) {
        const parts = t.split(":");
        if (parts.length >= 2) {
            return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
    }
    if (/^\d{4}$/.test(t)) {
        return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
    }
    return "";
};

// ---- DATETIME FORMATTER (for decisions) ----
const formatDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;

    return dt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// ---- STATUS COLOUR PILL (same as Episodes.jsx) ----
const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();

    switch (s) {
        case "planned":
            return { bg: "#e0f2fe", text: "#0369a1" };
        case "pending approval":
            return { bg: "#fef9c3", text: "#a16207" };
        case "scheduled":
            return { bg: "#dcfce7", text: "#166534" };
        case "approved":
            return { bg: "#d9f99d", text: "#4d7c0f" };
        case "published":
            return { bg: "#bbf7d0", text: "#15803d" };
        case "rejected":
            return { bg: "#fee2e2", text: "#b91c1c" };
        default:
            return { bg: "#e5e7eb", text: "#374151" };
    }
};

// ---- BUILD SAFE PUBLISH DATETIME (avoids NaN) ----
const buildPublishDateTime = (ep) => {
    if (!ep || !ep.publication_date) return null;

    // Always slice to YYYY-MM-DD in case we were given a full ISO string
    const datePart = String(ep.publication_date).slice(0, 10); // e.g. "2026-04-01"

    // Normalise time → HH:MM (fallback 00:00)
    const rawTime =
        typeof ep.publication_time === "string" ? ep.publication_time : "";
    let timePart = "";

    if (rawTime.includes(":")) {
        const parts = rawTime.split(":");
        const hh = (parts[0] || "00").padStart(2, "0");
        const mm = (parts[1] || "00").padStart(2, "0");
        timePart = `${hh}:${mm}`;
    } else if (/^\d{4}$/.test(rawTime)) {
        // "0930" → "09:30"
        timePart = `${rawTime.slice(0, 2)}:${rawTime.slice(2, 4)}`;
    } else {
        // If no usable time, assume midnight
        timePart = "00:00";
    }

    const isoLike = `${datePart}T${timePart}:00`;
    const dt = new Date(isoLike);

    if (Number.isNaN(dt.getTime())) {
        return null;
    }

    return dt;
};

function Dashboard({ onLogout, embedded = false }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [stats, setStats] = useState({
        clients: 0,
        podcasts: 0,
        episodes: 0,
        totalMonthlyCost: 0,
    });

    const [attentionEpisodes, setAttentionEpisodes] = useState([]);
    const [recentDecisions, setRecentDecisions] = useState([]);
    const [requestedEpisodes, setRequestedEpisodes] = useState([]);

    useEffect(() => {
        async function load() {
            try {
                const [clients, podcastsRes, eps] = await Promise.all([
                    getClients(),
                    getPodcasts(),
                    getEpisodes(),
                ]);

                const podcastList = podcastsRes?.podcasts || [];

                // ---- total monthly_cost across all podcasts ----
                const totalMonthlyCost = podcastList.reduce((sum, p) => {
                    const val = p?.monthly_cost;
                    const num = val === null || val === undefined ? 0 : Number(val);
                    return sum + (Number.isNaN(num) ? 0 : num);
                }, 0);

                // Stats
                setStats({
                    clients: clients.length || 0,
                    podcasts: podcastList.length || 0,
                    episodes: eps?.episodes?.length || 0,
                    totalMonthlyCost,
                });

                if (eps?.episodes) {
                    const now = new Date();
                    const tomorrow = new Date(
                        now.getTime() + 24 * 60 * 60 * 1000
                    )
                        .toISOString()
                        .slice(0, 10);

                    // Needs attention filter:
                    //  - Any episode with status Rejected (regardless of date)
                    //  - OR: pubDate <= tomorrow (past, today, tomorrow)
                    //    AND not scheduled / published / approved
                    const needsAttention = eps.episodes
                        .filter((ep) => {
                            const status = (ep.status || "").toLowerCase();
                            const isRejected = status === "rejected";

                            // Always include rejected episodes
                            if (isRejected) return true;

                            const pubDate = ep.publication_date
                                ? String(ep.publication_date).slice(0, 10)
                                : "";
                            if (!pubDate) return false;

                            const isCompleted =
                                status === "scheduled" ||
                                status === "published" ||
                                status === "approved";

                            if (isCompleted) return false;

                            // Include anything past or up to tomorrow
                            return pubDate <= tomorrow;
                        })
                        .sort((a, b) => {
                            const da = a.publication_date
                                ? new Date(a.publication_date)
                                : null;
                            const db = b.publication_date
                                ? new Date(b.publication_date)
                                : null;

                            if (!da && !db) return 0;
                            if (!da) return 1;
                            if (!db) return -1;
                            return da - db; // earliest first
                        });

                    setAttentionEpisodes(needsAttention);

                    // ---- episodes approved/rejected in the last 7 days ----
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

                    const recent = eps.episodes
                        .filter((ep) => {
                            const status = (ep.status || "").toLowerCase();
                            const isDecision =
                                status === "approved" || status === "rejected";

                            if (!isDecision) return false;

                            // Use client_reviewed_at as "decision date"
                            if (!ep.client_reviewed_at) return false;

                            const decidedAt = new Date(ep.client_reviewed_at);
                            if (Number.isNaN(decidedAt.getTime())) return false;

                            // last 7 days, up to "now"
                            return decidedAt >= sevenDaysAgo && decidedAt <= now;
                        })
                        .sort((a, b) => {
                            const da = a.client_reviewed_at
                                ? new Date(a.client_reviewed_at)
                                : 0;
                            const db = b.client_reviewed_at
                                ? new Date(b.client_reviewed_at)
                                : 0;
                            return db - da; // newest first
                        });

                    setRecentDecisions(recent);

                    const requested = eps.episodes
                        .filter(
                            (ep) =>
                                String(ep.status || "").toLowerCase().trim() ===
                                "requested"
                        )
                        .sort((a, b) => {
                            const da = Date.parse(String(a?.publication_date || ""));
                            const db = Date.parse(String(b?.publication_date || ""));
                            const safeDa = Number.isNaN(da) ? Number.MAX_SAFE_INTEGER : da;
                            const safeDb = Number.isNaN(db) ? Number.MAX_SAFE_INTEGER : db;
                            return safeDa - safeDb;
                        });

                    setRequestedEpisodes(requested);
                }
            } catch (err) {
                console.error(err);
                setError("Failed to load dashboard data");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    // ---- COUNTDOWN FORMATTER (months / weeks / days / hours) ----
    const getCountdown = (ep) => {
        const dt = buildPublishDateTime(ep);
        if (!dt) return null;

        const now = new Date();
        const diffMs = dt.getTime() - now.getTime();
        if (!Number.isFinite(diffMs)) {
            return null;
        }

        const MS_MIN = 60 * 1000;
        const MS_HOUR = 60 * MS_MIN;
        const MS_DAY = 24 * MS_HOUR;

        // Already in the past → overdue
        if (diffMs <= 0) {
            const overdueMs = Math.abs(diffMs);
            const overdueDays = Math.floor(overdueMs / MS_DAY);

            if (overdueDays <= 0) {
                return "Overdue";
            }
            return `${overdueDays}d overdue`;
        }

        const totalDays = Math.floor(diffMs / MS_DAY);

        // Long-range: show months + weeks/days (rough 30-day months)
        if (totalDays >= 60) {
            const months = Math.floor(totalDays / 30);
            const remainingDays = totalDays % 30;

            if (remainingDays >= 14) {
                const weeks = Math.floor(remainingDays / 7);
                return weeks > 0
                    ? `${months}mo ${weeks}w remaining`
                    : `${months}mo remaining`;
            }

            if (remainingDays > 0) {
                return `${months}mo ${remainingDays}d remaining`;
            }

            return `${months}mo remaining`;
        }

        // Medium-range: weeks + days
        if (totalDays >= 14) {
            const weeks = Math.floor(totalDays / 7);
            const days = totalDays % 7;

            if (days > 0) {
                return `${weeks}w ${days}d remaining`;
            }
            return `${weeks}w remaining`;
        }

        // Short-range: days
        if (totalDays >= 2) {
            return `${totalDays}d remaining`;
        }

        // Very short-range: hours and minutes
        const totalMinutes = Math.round(diffMs / MS_MIN);
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        if (!Number.isFinite(hours) || !Number.isFinite(mins)) {
            return null;
        }

        if (hours <= 0 && mins <= 0) return "Deadline soon";
        if (hours <= 0) return `${mins}m remaining`;

        return `${hours}h ${mins}m remaining`;
    };

    // Navigate to episodes page with modal open
    const openEpisode = (id) => {
        window.location.hash = `#/episodes/open?id=${id}`;
    };

    // Nicely formatted £ value
    const monthlyCostDisplay =
        typeof stats.totalMonthlyCost === "number"
            ? stats.totalMonthlyCost.toLocaleString("en-GB", {
                style: "currency",
                currency: "GBP",
            })
            : "£0.00";

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                {/* Header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Dashboard</h2>
                        <p className="portal-page__subtitle">
                            Overview & upcoming deadlines.
                        </p>
                    </div>
                </div>

                {error && <div className="alert alert--error">{error}</div>}
                {loading && <p>Loading dashboard…</p>}

                {!loading && !error && (
                    <>
                        {/* Stats */}
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(220px, 1fr))",
                                gap: "16px",
                                marginTop: "8px",
                            }}
                        >
                            <div className="card">
                                <h3 className="card__title">Clients</h3>
                                <p className="card__subtitle">Active clients</p>
                                <div className="stat-number">{stats.clients}</div>
                            </div>

                            <div className="card">
                                <h3 className="card__title">Podcasts</h3>
                                <p className="card__subtitle">Managed shows</p>
                                <div className="stat-number">{stats.podcasts}</div>
                            </div>

                            <div className="card">
                                <h3 className="card__title">Episodes</h3>
                                <p className="card__subtitle">All-time episodes</p>
                                <div className="stat-number">{stats.episodes}</div>
                            </div>

                            {/* Total monthly cost card */}
                            <div className="card">
                                <h3 className="card__title">Income</h3>
                                <p className="card__subtitle">
                                    Total monthly income
                                </p>
                                <div className="stat-number">
                                    {monthlyCostDisplay}
                                </div>
                            </div>
                        </div>

                        {/* Needs Attention */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">
                                Episodes Requiring Attention
                            </h3>
                            <p className="card__subtitle">
                                Upcoming/overdue episodes that aren&apos;t yet
                                scheduled/approved/published, plus any rejected episodes.
                            </p>

                            {attentionEpisodes.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No episodes need attention 🎉
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {attentionEpisodes.map((ep) => {
                                    const countdown = getCountdown(ep);
                                    const dateStr = formatPublishDateLabel(
                                        ep.publication_date
                                    );
                                    const timeStr = formatTime(ep.publication_time);
                                    const usesPublication = isDistributionEnabled(
                                        ep?.podcast_distribution
                                    );
                                    const statusColors = getStatusColor(ep.status);

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor: "#e63946",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {ep.publication_date ? (
                                                        <>
                                                            {usesPublication
                                                                ? `Publishes ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                                                                : `Deadline ${dateStr}`}
                                                            {countdown
                                                                ? ` • ${countdown}`
                                                                : ""}
                                                        </>
                                                    ) : (
                                                        usesPublication
                                                            ? "No publication date set"
                                                            : "No deadline date set"
                                                    )}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Requested episodes */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">Requested Episodes</h3>
                            <p className="card__subtitle">
                                Episodes currently waiting in Requested status.
                            </p>

                            {requestedEpisodes.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No episodes are currently in Requested.
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {requestedEpisodes.map((ep) => {
                                    const statusColors = getStatusColor(ep.status);
                                    const dateStr = formatPublishDateLabel(
                                        ep.publication_date
                                    );
                                    const timeStr = formatTime(ep.publication_time);
                                    const usesPublication = isDistributionEnabled(
                                        ep?.podcast_distribution
                                    );

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor: "#6d28d9",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {ep.publication_date
                                                        ? usesPublication
                                                            ? `Publishes ${dateStr}${timeStr ? ` at ${timeStr}` : ""}`
                                                            : `Deadline ${dateStr}`
                                                        : usesPublication
                                                            ? "No publication date set"
                                                            : "No deadline date set"}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Recent approvals/rejections */}
                        <div className="card" style={{ marginTop: 20 }}>
                            <h3 className="card__title">
                                Recent Approvals & Rejections
                            </h3>
                            <p className="card__subtitle">
                                Episodes approved or rejected in the last 7 days
                                that haven&apos;t yet moved on to scheduling or
                                publishing.
                            </p>

                            {recentDecisions.length === 0 && (
                                <p style={{ marginTop: 10, color: "#6b7280" }}>
                                    No recent approvals or rejections in the last 7 days.
                                </p>
                            )}

                            <div className="episode-list" style={{ marginTop: 12 }}>
                                {recentDecisions.map((ep) => {
                                    const statusColors = getStatusColor(ep.status);
                                    const decidedAt = ep.client_reviewed_at
                                        ? formatDateTime(ep.client_reviewed_at)
                                        : "";

                                    return (
                                        <div
                                            key={ep.id}
                                            className="episode-card"
                                            style={{
                                                borderLeftColor:
                                                    (ep.status || "")
                                                        .toLowerCase() === "approved"
                                                        ? "#16a34a"
                                                        : "#dc2626",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => openEpisode(ep.id)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "—";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                <div className="episode-card__meta">
                                                    {decidedAt
                                                        ? `${ep.status} on ${decidedAt}`
                                                        : ep.status}
                                                </div>
                                            </div>
                                            <div className="episode-card__right">
                                                <span
                                                    className="pill pill--role-client"
                                                    style={{
                                                        background: statusColors.bg,
                                                        color: statusColors.text,
                                                    }}
                                                >
                                                    {ep.status || "Draft"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </PageShell>
    );
}

export default Dashboard;
